import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 🔋 PowerMeter (09-26): the house's AI balance as a battery with the hours
 * left. Nina first put it "by the clock" (mockup
 * https://claude.ai/artifact/DxmzXZxsGyiB5pYMgew3tU, option A + hours), then
 * moved it the same day: "in the place of the money bag at the top right".
 * So it is the last chip of the masthead's vitals row: on desktop it takes the
 * 💰 till pill's spot and shape (the till stays on the 📌 memory wall), on
 * phones it joins the ❤️/👎 row in their chip shape (`compact`).
 * `view` = utils/powerMeter.meterView(status.ai_power) or null: nothing
 * renders (the backend flag is off or the reading is missing) and the till
 * pill keeps its spot.
 *
 * A tap/click opens one short honest note (what the battery measures, what
 * happens at empty, that notes wait). The support link shows only when the
 * view carries it, and it always carries its note (Nina 09-26: the money only
 * runs the house and pays nobody).
 *
 * 🔋 09-29 the 5 AM / 5 PM recharge (only when the backend sends the window):
 * the note gets a countdown row under its head ("Recharges at 5:00 PM · in
 * 3h 12m", with "(5 PM Phoenix)" small when the viewer's clock differs), and
 * the chip may read "Recharging at 5 PM" (low, the recharge lands first) or
 * "💤 Back at 5 PM" (a sleep we scheduled). A real $0 stays a bare 💤.
 *
 * 💛 09-29 the funding meter (Nina's option C; only when the view carries
 * `funding`, which rides only beside the support link): "This month, visitors
 * covered N% of the AI bill" + a thin gold bar, at the top of the support
 * block, right above "Help keep the lights on in {space}" and its note.
 *
 * 💳 10-28 Chip in, real money (STRIPE_TIPS_LANE_2026_10_28.md; Nina: "Beside
 * the battery", "Every room", "Hold tips back"): when the view carries
 * `chipIn`, a word button "Chip in" sits right beside the chip (a plain link to
 * Stripe's page with this room on it, a new tab), in the pill's shape on
 * desktop and the ❤️/👎 chip shape on phones. The note shows this room's
 * backup (`view.backup`: tips held back for it) above the support block, and a
 * mod (the page passes `tipsSwitch`) gets "Tips in this room: On / Off" at the
 * bottom, shown whenever a link exists, so a room switched off can come back.
 * Every new element styles itself inline: themes style by class SUBSTRING.
 *
 * The identity block is overflow:hidden and the house theme's customCSS gives
 * EVERY button position:relative + overflow:hidden: the note renders in a
 * PORTAL on document.body, fixed under the chip (over it when the chip sits in
 * the lower half of the screen), centered on it and kept inside the gutters.
 * ⚠️ This frontend has NO global box-sizing: every padded box sets it.
 */
const NOTE_W = 272;
const GUTTER = 16;

export function Battery({ fill = 1, color = 'currentColor', w = 19, h = 10 }) {
  return (
    <span aria-hidden="true" data-battery={Math.round(fill * 100)} style={{
      position: 'relative', display: 'inline-block', flexShrink: 0, boxSizing: 'border-box',
      width: w, height: h, border: `1.5px solid ${color}`, borderRadius: 3, marginRight: 3,
    }}>
      <span style={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)', width: 2, height: Math.round(h * 0.4), background: color, borderRadius: '0 1px 1px 0' }} />
      <span data-testid="battery-fill" style={{ position: 'absolute', left: 1.5, top: 1.5, bottom: 1.5, width: `calc((100% - 3px) * ${Math.max(0, Math.min(1, fill)).toFixed(2)})`, background: color, borderRadius: 1 }} />
    </span>
  );
}

function ClockGlyph() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** 💳 "Tips in this room: On / Off": the mods' switch (the page owns the request). */
function TipsSwitch({ on, busy = false, error = null, onToggle }) {
  return (
    <span data-power-tips={on ? 'on' : 'off'} style={{ display: 'grid', gap: 4, paddingTop: 6, borderTop: '1px solid rgba(196,149,106,0.2)' }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 12, color: '#d9cdbb' }}>Tips in this room</span>
        <button type="button" role="switch" aria-checked={on ? 'true' : 'false'} aria-label="Tips in this room"
          data-power-tips-switch="1" disabled={busy}
          onClick={() => { if (!busy && typeof onToggle === 'function') onToggle(!on); }}
          style={{
            boxSizing: 'border-box', minWidth: 52, padding: '3px 10px', borderRadius: 999, cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, letterSpacing: 'normal', textTransform: 'none',
            border: `1px solid ${on ? 'rgba(159,212,174,0.7)' : 'rgba(255,255,255,0.25)'}`,
            background: on ? 'rgba(159,212,174,0.16)' : 'rgba(255,255,255,0.06)', color: on ? '#9fd4ae' : '#a89b8c',
            opacity: busy ? 0.6 : 1,
          }}>
          {on ? 'On' : 'Off'}
        </button>
      </span>
      {error ? <span role="status" style={{ fontSize: 11, color: '#ff8f7d' }}>{error}</span> : null}
    </span>
  );
}

export default function PowerMeter({ view, compact = false, tipsSwitch = null }) {
  const [open, setOpen] = useState(false);
  const [spot, setSpot] = useState(null); // { left, width, top | bottom } in viewport px
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const noteRef = useRef(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      const width = Math.min(NOTE_W, Math.max(0, vw - GUTTER * 2)) || NOTE_W;
      const r = btnRef.current ? btnRef.current.getBoundingClientRect() : null;
      if (!r) { setSpot({ left: GUTTER, width, top: GUTTER }); return; }
      const left = Math.max(GUTTER, Math.min(r.left + r.width / 2 - width / 2, vw - width - GUTTER));
      // 09-29 (a Promote link's ?charge=1 opens this note on arrival): a phone's
      // page scrolls and opens on the newest line, so the chip can sit wholly
      // above the screen. The note keeps to the screen then (under the phone's
      // Back · ♫ row) instead of hanging off the top with the chip. (A chip
      // below the screen already lands on the bottom gutter.)
      if (r.bottom < 0) { setSpot({ left, width, top: 64 }); return; }
      if (r.top + r.height / 2 <= vh / 2) setSpot({ left, width, top: r.bottom + 8 });
      else setSpot({ left, width, bottom: Math.max(GUTTER, vh - r.top + 8) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => {
      const t = e.target;
      if (!t) return;
      if (wrapRef.current && wrapRef.current.contains(t)) return;
      if (noteRef.current && noteRef.current.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);
  if (!view) return null;
  const tone = view.color;
  const side = spot ? (spot.top != null ? { top: spot.top } : { bottom: spot.bottom }) : { top: GUTTER };
  // 💳 Chip in: a word, beside the chip, in the same shape as its row
  const chipInStyle = compact ? {
    display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: 6,
    background: 'rgba(240,201,106,0.16)', border: 'none', color: '#f0c96a',
    fontSize: '0.75rem', fontWeight: 600,
  } : {
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999,
    fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.3px',
    background: 'rgba(0,0,0,0.42)', border: '1px solid rgba(240,201,106,0.7)', color: '#f0c96a',
    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
  };
  const chip = compact ? {
    // the phone row's ❤️/👎 chip
    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
    background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 6,
    boxShadow: open ? `inset 0 0 0 1px ${tone}` : 'none',
    fontSize: '0.75rem', fontWeight: 600,
  } : {
    // the 💰 till pill it replaces
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999,
    fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.3px',
    background: 'rgba(0,0,0,0.42)', border: `1px solid ${tone}${open ? 'f2' : '8c'}`,
    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
  };
  return (
    // 💳 Chip in only adds to the wrapper: no Chip in = exactly the old style, so the battery still
    // stretches to its row (the phone ❤️/👎 row is a stretch row: the runner's #215 review). A phone
    // keeps stretching beside Chip in too; only the desktop pill centers on the battery.
    <span ref={wrapRef} data-power-meter={view.level} style={{
      display: 'inline-flex', flex: '0 0 auto', minWidth: 0,
      ...(view.chipIn ? { gap: compact ? 8 : 6, ...(compact ? {} : { alignItems: 'center' }) } : {}),
    }}>
      <button type="button" data-power-chip="1" ref={btnRef}
        aria-label={view.aria} aria-haspopup="dialog" aria-expanded={open} title={view.head}
        onClick={() => setOpen((o) => !o)}
        style={{
          ...chip, boxSizing: 'border-box', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.2,
          whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: tone,
        }}>
        <Battery fill={view.fill} color={tone} w={compact ? 19 : 17} h={compact ? 10 : 9} />
        {view.asleep && <span aria-hidden="true">💤</span>}
        {view.text ? <span aria-hidden="true">{view.text}</span> : null}
      </button>
      {view.chipIn && (
        <a href={view.chipIn.url} target="_blank" rel="noopener noreferrer" data-chip-in-btn={compact ? 'compact' : '1'}
          aria-label="Chip in to keep this room on" title="Chip in to keep this room on"
          style={{
            ...chipInStyle, boxSizing: 'border-box', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.2,
            whiteSpace: 'nowrap', textDecoration: 'none', flex: '0 0 auto',
          }}>
          {view.chipIn.label}
        </a>
      )}
      {open && typeof document !== 'undefined' && createPortal(
        <span ref={noteRef} role="dialog" aria-label={view.head} data-power-note="1" style={{
          position: 'fixed', zIndex: 10000, left: spot ? spot.left : GUTTER, ...side,
          boxSizing: 'border-box', width: spot ? spot.width : NOTE_W, maxWidth: `calc(100vw - ${GUTTER * 2}px)`,
          display: 'grid', gap: 7, padding: '12px 14px', fontFamily: 'inherit',
          background: '#2f2a24', border: '1px solid rgba(196,149,106,0.34)', borderRadius: 14,
          boxShadow: '0 10px 30px rgba(0,0,0,0.45)', color: '#d9cdbb', textAlign: 'left',
          fontSize: 13, fontWeight: 400, lineHeight: 1.45, whiteSpace: 'normal',
          letterSpacing: 'normal', textTransform: 'none', textShadow: 'none',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#f0e6d9', fontWeight: 700, fontSize: 14 }}>
            <Battery fill={view.fill} color={view.color} />
            {view.head}
          </span>
          {view.recharge && (
            <span data-power-recharge="1" style={{ display: 'grid', gap: 1 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f0e6d9', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                <ClockGlyph />
                {view.recharge.line}
              </span>
              {view.recharge.phoenix && <span style={{ paddingLeft: 19, fontSize: 11, color: '#a89b8c' }}>{view.recharge.phoenix}</span>}
            </span>
          )}
          <span>{view.body}</span>
          {view.foot && <span style={{ fontSize: 11, color: '#a89b8c' }}>{view.foot}</span>}
          {view.backup && (
            <span data-power-backup={view.backup.active ? 'active' : '1'} style={{ display: 'grid', gap: 5 }}>
              <span style={{ color: view.backup.active ? '#f0e6d9' : '#d9cdbb', fontWeight: view.backup.active ? 600 : 400, fontSize: 12.5, lineHeight: 1.35 }}>{view.backup.line}</span>
              <span aria-hidden="true" style={{
                position: 'relative', display: 'block', boxSizing: 'border-box', height: 6, borderRadius: 3,
                overflow: 'hidden', background: 'rgba(255,236,210,0.1)',
              }}>
                <span data-backup-fill={Math.round(view.backup.frac * 100)} style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(view.backup.frac * 100).toFixed(1)}%`,
                  borderRadius: 3, background: 'linear-gradient(90deg, #7fbf91, #9fd4ae)',
                }} />
              </span>
            </span>
          )}
          {view.support && (
            <span data-power-support="1" style={{ display: 'grid', gap: 5, paddingTop: 4, borderTop: '1px solid rgba(196,149,106,0.2)' }}>
              {view.funding && (
                <span data-power-funding={view.funding.full ? 'full' : '1'} style={{ display: 'grid', gap: 5, paddingTop: 2, marginBottom: 3 }}>
                  <span style={{ color: '#f0e6d9', fontWeight: 600, fontSize: 12.5, lineHeight: 1.35 }}>{view.funding.line}</span>
                  <span aria-hidden="true" style={{
                    position: 'relative', display: 'block', boxSizing: 'border-box', height: 6, borderRadius: 3,
                    overflow: 'hidden', background: 'rgba(255,236,210,0.1)',
                  }}>
                    <span data-funding-fill={Math.round(view.funding.frac * 100)} style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(view.funding.frac * 100).toFixed(1)}%`,
                      borderRadius: 3, background: 'linear-gradient(90deg, #d9a94e, #f0c96a)',
                    }} />
                  </span>
                </span>
              )}
              <a href={view.support.url} target="_blank" rel="noopener noreferrer"
                style={{ justifySelf: 'start', fontWeight: 700, color: '#e9b565', textDecoration: 'none' }}>
                {view.support.label} →
              </a>
              <span style={{ fontSize: 11.5, color: '#a89b8c' }}>{view.support.note}</span>
            </span>
          )}
          {tipsSwitch && view.tips && (
            <TipsSwitch on={view.tips.on} busy={!!tipsSwitch.busy} error={tipsSwitch.error || null} onToggle={tipsSwitch.onToggle} />
          )}
        </span>,
        document.body
      )}
    </span>
  );
}
